import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import { APIURL, ONBOARDAPIURL } from '../../../config';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Typography, Box, IconButton, Button, Tabs, Tab, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, MenuItem, Tooltip } from '@mui/material';
import PersonalInformation from '../General/PersonalInformation';
import EmploymentInformation from '../General/EmploymentInformation';
import EmployeeTaskType from '../General/EmployeeTaskType';
import CustomerPreferences from '../General/CustomerPreferences';
import EmployeeCertifications from '../General/EmployeeCertifications';
import EmployeeDocuments from '../General/EmployeeDocuments';
import EmployeeTimeEntries from '../General/EmployeeTimeEntries';
import EmployeeLeaveInformation from '../General/EmployeeLeaveInformation';
import ViewEmployeeRelationship from '../General/ViewEmployeeRelationship';
import SecureCommunication2 from '../General/SecureCommunication2';
import CaregiverPreferences from '../General/CaregiverPreferences';
import CustomerAssignment from './CustomerAssignment';
import Scheduler from '../../../container/Scheduler/Scheduler';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import Switch from '@mui/material/Switch';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const EmpSettings = (props) => {
    const selfServiceMode = Boolean(props.selfServiceMode);
    const initialEmployee = selfServiceMode
        ? props.empList?.find((employee) => String(employee.emp_id) === String(props.user_id)) || props.empList?.[0] || null
        : null;
    const [empList, setEmpList] = useState(props.empList || []);
    const [selectedEmp, setSelectedEmp] = useState(initialEmployee);
    const [pageSize, setPageSize] = useState(13);
    const [viewMode, setViewMode] = useState(Boolean(initialEmployee));
    const [activeTab, setActiveTab] = useState(0);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [empToDelete, setEmpToDelete] = useState(null);
    const [empTypeDialogOpen, setEmpTypeDialogOpen] = useState(false);
    const [newEmpType, setNewEmpType] = useState('');
    const [empTypeList, setEmpTypeList] = useState([]);
    const [rowSelectionModel, setRowSelectionModel] = useState({ type: 'include', ids: new Set() });
    const [designationEmployeeIds, setDesignationEmployeeIds] = useState([]);
    const [savingDesignation, setSavingDesignation] = useState(false);
    const [designationError, setDesignationError] = useState('');
    const savingDesignationRef = useRef(false);
    const [employeeTaskTypeList, setEmployeeTaskTypeList] = useState([]);
    const [alertSeverity, setAlertSeverity] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [communicationSettings, setCommunicationSettings] = useState({
        mtSubmission: false,
        mtRejected: false,
        mtApproved: false,
        taskAssigned: false,
        taskStartTimeApproaching: false,
        taskEndTimeApproaching: false,
    });

    const canManageEmployees = props.canManageEmployees !== false;
    const selectedEmployeeIds = (empList || []).filter((emp) => rowSelectionModel.ids.has(emp.emp_id)).map((emp) => emp.emp_id);

    const openDesignationDialog = (employeeIds) => {
        setDesignationEmployeeIds(employeeIds);
        setNewEmpType('');
        setDesignationError('');
        setEmpTypeDialogOpen(true);
    };

    const closeDesignationDialog = () => {
        if (!savingDesignationRef.current) setEmpTypeDialogOpen(false);
    };

    useEffect(() => {
        setRowSelectionModel({ type: 'include', ids: new Set() });
        setEmpTypeDialogOpen(false);
    }, [props.comp_id]);

    const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));

    async function handleSosToggle(id, status) {
        const empUpdateResp = await axios.put(APIURL + '/employees', {
            emp_id: id,
            sosUpdate: true,
            emp_sos_status: status ? 1 : 0,
        })
        setEmpList(empList.map((emp) => {
            return {
                ...emp,
                emp_sos_status: emp.emp_id === id ? (status ? 1 : 0) : emp.emp_sos_status,
            }
        }))
    }

    const getEmployeeTaskTypes = async (emp_id, comp_id) => {
        let empTasks = await axios.get(APIURL + "/employees/EmployeeTasks", {
            params: { comp_id: props.comp_id, employee_id: emp_id },
        });

        let taskTypeList = await axios.get(APIURL + "/dropdowns/dropdownType/fullTaskType", {
            params: { comp_id: comp_id },
        });

        taskTypeList.data.forEach((type) => {
            if (empTasks.data.find((task) => task.ddID === type.ddID)) {
                taskTypeList.data.find((task) => task.ddID === type.ddID).is_Active = 0
            } else {
                taskTypeList.data.find((task) => task.ddID === type.ddID).is_Active = 1
            }
        })
        setEmployeeTaskTypeList(taskTypeList.data)
    }

    const getCommunicationSettings = async (empId) => {
        const featureKeys = [
            [1, 'mtSubmission'],
            [2, 'mtRejected'],
            [3, 'mtApproved'],
            [4, 'taskAssigned'],
            [5, 'taskStartTimeApproaching'],
            [6, 'taskEndTimeApproaching'],
        ];

        try {
            const responses = await Promise.all(featureKeys.map(([featureId]) =>
                axios.get(`${APIURL}/config/settings/${empId}`, {
                    params: { comp_id: props.comp_id, feature_id: featureId },
                })
            ));
            setCommunicationSettings(featureKeys.reduce((settings, [, key], index) => ({
                ...settings,
                [key]: responses[index].data?.[0]?.feature_value === 'true',
            }), {}));
        } catch (error) {
            console.error('Unable to load employee communication settings', error);
        }
    };

    useEffect(() => {
        const fetchEmpTypes = async () => {
            try {
                const response = await axios.get(APIURL + `/dropdowns/dropdownType/empType?comp_id=${props.comp_id}`);
                setEmpTypeList(response.data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchEmpTypes();
    }, [props.comp_id]);

    useEffect(() => {
        setEmpList(props.empList);
        if (selfServiceMode) {
            const employee = props.empList?.find((item) => String(item.emp_id) === String(props.user_id)) || props.empList?.[0] || null;
            setSelectedEmp((currentEmployee) => currentEmployee || employee);
            setViewMode(Boolean(employee));
        }
    }, [props.empList, props.user_id, selfServiceMode]);

    useEffect(() => {
        if (!selectedEmp) return;
        let tempEmpList = [...empList];
        let index = tempEmpList.findIndex((emp) => emp.emp_id === selectedEmp.emp_id);
        tempEmpList[index] = selectedEmp;

        setEmpList(tempEmpList);

        getEmployeeTaskTypes(selectedEmp.emp_id, props.comp_id);
        getCommunicationSettings(selectedEmp.emp_id);
    }, [selectedEmp]);

    if (!empList || empList.length === 0) {
        return (
            <Box display="flex" justifyContent="center" width="50%">
                <Typography>No Employee Data</Typography>
            </Box>
        );
    }

    const columns = [
        {
            field: 'emp_name',
            headerName: 'NAME',
            width: 165,
            renderCell: (params) => (
                <Typography style={{ lineHeight: "1", marginTop: "10px" }}>{params.value}</Typography>
            )
        },
        {
            field: 'emp_phone',
            headerName: 'PHONE',
            width: 110,
        },
        {
            field: 'emp_email',
            headerName: 'EMAIL',
            width: 220,
        },
        {
            field: 'emp_type',
            headerName: 'DESIGNATION',
            width: 165,
        },
        {
            field: 'emp_status',
            headerName: 'STATUS',
            width: 120,
            renderCell: (params) => (
                <Box display="flex" alignItems="center">
                    <Box
                        sx={{
                            border: `1px solid ${params.value ? '#2e7d32' : '#d32f2f'}`,
                            backgroundColor: params.value ? '#2e7d32' : '#d32f2f',
                            borderRadius: '50%',
                            width: '0.5rem',
                            height: '0.5rem',
                            mr: 1
                        }}
                    />
                    <Typography>{params.value ? 'Active' : 'Terminated'}</Typography>
                </Box>
            )
        },
        {
            field: 'emp_join_date',
            headerName: 'JOIN DATE',
            width: 100,
            sortComparator: dateComparator,
        },
        {
            field: 'emp_updated_at',
            headerName: 'LAST UPDATED',
            width: 100,
            sortComparator: dateComparator,
        },
        {
            field: 'sos_eligible',
            headerName: 'SOS STATUS',
            width: 100,
            renderCell: (params) => (
                <Tooltip title={canManageEmployees ? 'Change SOS eligibility' : 'SOS eligibility'}>
                    <span>
                        <Switch checked={Boolean(params.value)} disabled={!canManageEmployees} onChange={(e) => handleSosToggle(params.id, e.target.checked)} />
                    </span>
                </Tooltip>
            ),
        },
        ...(props.canViewInternalSupport ? [{
            field: 'emp_is_internal_support',
            headerName: 'INTERNAL SUPPORT',
            type: 'boolean',
            width: 155,
            editable: false,
            renderCell: (params) => (
                <Typography>{params.value ? 'Yes' : 'No'}</Typography>
            ),
        }] : []),
        {
            field: 'details',
            headerName: 'ACTIONS',
            width: 190,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Box>
                    <Tooltip title="View employee details">
                        <IconButton
                            color="primary"
                            aria-label="View employee details"
                            onClick={() => {
                                const employee = empList.find((emp) => emp.emp_id === params.row.id);
                                setSelectedEmp(employee);
                                setActiveTab(0);
                                setViewMode(true);
                                props.onDetailViewChange?.(employee);
                            }}
                        >
                            <VisibilityIcon />
                        </IconButton>
                    </Tooltip>
                    {canManageEmployees && <>
                        <Tooltip title="Change employee designation">
                            <IconButton
                                color="primary"
                                aria-label="Change employee designation"
                                onClick={() => {
                                    openDesignationDialog([params.row.id]);
                                }}
                            >
                                <PersonIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Send a recovery email to this user">
                            <IconButton
                                size="small"
                                color="primary"
                                aria-label="Send account recovery email"
                                onClick={() => {
                                    handleSendRecoveryEmail(params.row.id, params.row.sec_email);
                                }}
                            >
                                <ForwardToInboxIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete employee">
                            <IconButton
                                color="primary"
                                aria-label="Delete employee"
                                onClick={() => {
                                    setEmpToDelete(params.row.id);
                                    setDeleteDialogOpen(true);
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </>}
                </Box>
            )
        }
    ];

    const rows = empList.map((emp) => {
        let phnRec = emp.emp_phone_number ? emp.emp_phone_number.replace(/[.-]/g, '').split('').filter((c) => c !== ' ').join('') : '-';

        return {
            id: emp.emp_id,
            ...(props.canViewInternalSupport ? {
                emp_is_internal_support: [true, 1, '1', 'true'].includes(emp.emp_is_internal_support),
            } : {}),
            sos_eligible: emp.emp_sos_status === 1 ? 1 : 0,
            emp_name: `${emp.emp_first_name} ${emp.emp_last_name}`,
            emp_phone: phnRec.length === 10 ? `${phnRec.slice(0, 3)}-${phnRec.slice(3, 6)}-${phnRec.slice(6)}` : phnRec.length === 12 ? `${phnRec.slice(2, 5)}-${phnRec.slice(5, 8)}-${phnRec.slice(8)}` : '-',
            emp_email: emp.empEmail,
            sec_email: emp.sec_email,
            emp_type: emp.emp_type,
            emp_status: emp.emp_is_active,
            emp_updated_at: emp.emp_updated_at ? moment(emp.emp_updated_at).format('MM/DD/YYYY') : '-',
            emp_join_date: emp.emp_join_date ? moment(emp.emp_join_date).format('MM/DD/YYYY') : '-'
        };
    });

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleDelete = async () => {
        try {
            await axios.delete(APIURL + '/employees', { data: { bval: 2, id: empToDelete } });
            let tempEmpList = [...empList];
            let index = tempEmpList.findIndex((emp) => emp.emp_id === empToDelete);
            tempEmpList.splice(index, 1);
            setEmpList(tempEmpList);
            setSelectedEmp(null);
            setViewMode(false);
            setDeleteDialogOpen(false);
        } catch (error) {
            console.error(error);
        }
    };

    const handleEmpTypeChange = async () => {
        if (!canManageEmployees || !newEmpType || designationEmployeeIds.length === 0 || savingDesignationRef.current) return;
        savingDesignationRef.current = true;
        setSavingDesignation(true);
        setDesignationError('');
        try {
            const response = await axios.put(APIURL + '/team/', { empList: designationEmployeeIds, type: newEmpType });
            if (response.data?.code && response.data.code !== 200) {
                throw new Error(response.data.message || 'Unable to update employee designations. Please try again.');
            }

            setEmpList((current) => current.map((emp) => designationEmployeeIds.includes(emp.emp_id) ? { ...emp, emp_type: newEmpType } : emp));
            props.onDesignationChange?.(designationEmployeeIds, newEmpType);
            setRowSelectionModel({ type: 'include', ids: new Set() });
            setEmpTypeDialogOpen(false);
            setAlertSeverity('success');
            setAlertMessage(`Designation updated for ${designationEmployeeIds.length} employee${designationEmployeeIds.length === 1 ? '' : 's'}.`);

            if (designationEmployeeIds.some((id) => String(id) === String(props.user_id))) {
                window.location.reload();
            }
        } catch (error) {
            setDesignationError(error.response?.data?.message || error.message || 'Unable to update employee designations. Please try again.');
        } finally {
            savingDesignationRef.current = false;
            setSavingDesignation(false);
        }
    };

    const handleSendRecoveryEmail = async (empId, recoveryEmail) => {
        if (!recoveryEmail?.trim()) {
            setAlertSeverity('error');
            setAlertMessage('No Secondary Email present for this user!');

            return;
        }

        try {
            let recoveryResponse = await axios.get(ONBOARDAPIURL + '/employee/recovery', {
                // By employee, not by email: one address can be the recovery email of
                // many employees across companies, and each would get an email.
                params: { emp_id: empId }
            }).then(() => {
                setAlertSeverity('success');
                setAlertMessage('Account Recovery Email Sent!');
            })
        } catch (err) {
            console.log(err)
            setAlertSeverity('error');
            setAlertMessage('Error while sending the email!');
        }
    }

    const handleCloseAlert = () => {
        setAlertSeverity('');
        setAlertMessage('');
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%', width: '100%' }}>
            {viewMode ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', pr: 0.5 }}>
                    {!selfServiceMode && (
                        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={() => {
                                    setViewMode(false);
                                    setSelectedEmp(null);
                                    props.onDetailViewChange?.(null);
                                }}
                                sx={{ textTransform: 'none' }}
                            >
                                Back to employees
                            </Button>
                        </Box>
                    )}
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="Employee Details Tabs" variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                        sx={{ '& > .MuiTabs-scrollButtons:first-child.Mui-disabled': { display: 'none' } }}>
                        <Tab label="Personal & Employment" />
                        <Tab label="Tasks, Relationships & Messages" />
                        {!selfServiceMode && <Tab label="Certifications & Documents" />}
                        {!selfServiceMode && <Tab label={canManageEmployees ? "Customer Preferences & Relationships" : "Customer Preferences"} />}
                        {!selfServiceMode && <Tab label="Time & Leave" />}
                        {!selfServiceMode && <Tab label="Schedule" />}
                    </Tabs>
                    {activeTab === 0 && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <PersonalInformation employee={selectedEmp} setEmployee={setSelectedEmp} user_id={selectedEmp.emp_id} comp_id={props.comp_id} />
                        <EmploymentInformation employee={selectedEmp} setEmployee={setSelectedEmp} empList={empList} levelList={props.levelList} compensationType={props.compensationType} statusList={props.statusList} user_id={selectedEmp.emp_id} comp_id={props.comp_id} type={props.type} hideLeaveInformation />
                        {props.businessType === 103 && <CaregiverPreferences employee={selectedEmp} com_id={props.comp_id} type={props.type} leftColumnSize={props.leftColumnSize} rightColumnSize={props.rightColumnSize} />}
                    </Box>}
                    {activeTab === 1 && <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <EmployeeTaskType leftColumnSize={props.leftColumnSize} rightColumnSize={props.rightColumnSize} user_id={selectedEmp.emp_id} comp_id={props.comp_id} typeList={employeeTaskTypeList} />
                        <ViewEmployeeRelationship user_id={selectedEmp.emp_id} comp_id={props.comp_id} empList={empList} leftColumnSize={props.leftColumnSize} rightColumnSize={props.rightColumnSize} />
                        <SecureCommunication2
                            {...communicationSettings}
                            user_id={selectedEmp.emp_id}
                            emp_id={props.user_id}
                            comp_id={props.comp_id}
                            leftColumnSize={props.leftColumnSize}
                            rightColumnSize={props.rightColumnSize}
                        />
                    </Box>}
                    {!selfServiceMode && activeTab === 2 && <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <EmployeeCertifications employee={selectedEmp} />
                        <EmployeeDocuments employee={selectedEmp} />
                    </Box>}
                    {!selfServiceMode && activeTab === 3 && <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <CustomerPreferences employee={selectedEmp} comp_id={props.comp_id} />
                        {canManageEmployees && <CustomerAssignment
                            comp_id={props.comp_id}
                            empList={props.assignmentEmpList || empList}
                            initialEmployeeId={selectedEmp.emp_id}
                            lockEmployee
                            compact
                            leftColumnSize={props.leftColumnSize}
                            rightColumnSize={props.rightColumnSize}
                        />}
                    </Box>}
                    {!selfServiceMode && activeTab === 4 && <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <EmployeeLeaveInformation employee={selectedEmp} setEmployee={setSelectedEmp} comp_id={props.comp_id} canEdit={canManageEmployees} />
                        <EmployeeTimeEntries employee={selectedEmp} />
                    </Box>}
                    {!selfServiceMode && activeTab === 5 && props.user && <Scheduler
                        {...props}
                        user={props.user}
                        comp_id={props.comp_id}
                        selectedView="employee"
                        selectedEmployeeId={selectedEmp.emp_id}
                    />}
                </Box>
            ) : (
                <Box sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    {canManageEmployees && selectedEmployeeIds.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', pb: 2 }}>
                            <Typography>{selectedEmployeeIds.length} selected</Typography>
                            <Button variant="contained" startIcon={<PersonIcon />} onClick={() => openDesignationDialog(selectedEmployeeIds)}>
                                Change designation
                            </Button>
                            <Button onClick={() => setRowSelectionModel({ type: 'include', ids: new Set() })}>Clear selection</Button>
                        </Box>
                    )}
                    <Box sx={{ flex: 1, minHeight: 360, width: '100%' }}>
                        <DataGridPro
                            initialState={{ density: 'compact' }}
                            rows={rows}
                            columns={columns}
                            rowSelection={canManageEmployees}
                            checkboxSelection={canManageEmployees}
                            disableRowSelectionOnClick
                            disableRowSelectionExcludeModel
                            rowSelectionModel={rowSelectionModel}
                            onRowSelectionModelChange={setRowSelectionModel}
                            pagination
                            pageSize={pageSize}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            pageSizeOptions={[pageSize, pageSize * 2, pageSize * 3]}
                            sx={{ height: '100%', width: '100%' }}
                            showToolbar />
                    </Box>
                </Box>
            )}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this employee?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={empTypeDialogOpen}
                onClose={closeDesignationDialog}
            >
                <DialogTitle>Choose Designation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Choose a designation for {designationEmployeeIds.length} selected employee{designationEmployeeIds.length === 1 ? '' : 's'}.
                    </DialogContentText>
                    {designationError && <Alert severity="error" sx={{ mt: 2 }}>{designationError}</Alert>}
                    <TextField
                        select
                        label="Employee Type"
                        value={newEmpType}
                        disabled={savingDesignation}
                        onChange={(e) => setNewEmpType(e.target.value)}
                        fullWidth
                        sx={{ mt: 2, width: 300 }}
                    >
                        {empTypeList.map((type, i) => (
                            <MenuItem key={i} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDesignationDialog} disabled={savingDesignation} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleEmpTypeChange} disabled={!newEmpType || savingDesignation || designationEmployeeIds.length === 0} color="primary">
                        {savingDesignation ? 'Saving…' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={alertSeverity && alertMessage} autoHideDuration={5000} onClose={handleCloseAlert} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert
                    onClose={handleCloseAlert}
                    severity={alertSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default EmpSettings;
